import { db } from '@/db/client';
import { updatePlayerCacheFromEvents } from '@/db/player-events';
import { handleBackgroundError } from '@/lib/error-handler';
import { PlayerEventSchema, type PlayerEvent } from '@/schemas';
import { parsePlayerId } from '@/schemas/ids';

export interface MergeResult {
  applied: number;
  replaced: number;
}

/**
 * Compare two player events for last-write-wins conflict resolution.
 * Returns true if incoming event should replace existing event.
 */
function shouldReplacePlayerEvent(existing: PlayerEvent, incoming: PlayerEvent): boolean {
  // If timestamps differ, newer timestamp wins
  if (incoming.timestamp !== existing.timestamp) {
    return incoming.timestamp > existing.timestamp;
  }

  // If timestamps are equal, use userId as tie-breaker (deterministic ordering)
  // Events with userId win over events without userId
  if (incoming.userId && !existing.userId) {
    return true;
  }
  if (!incoming.userId && existing.userId) {
    return false;
  }
  if (incoming.userId && existing.userId) {
    // Both have userId - use lexicographic comparison for deterministic ordering
    return incoming.userId > existing.userId;
  }

  // Both have no userId - use event ID as final tie-breaker
  return incoming.id > existing.id;
}

export async function mergeRemotePlayerEvents(incoming: PlayerEvent[]): Promise<MergeResult> {
  if (incoming.length === 0) return { applied: 0, replaced: 0 };
  const validated = incoming.map((e) => PlayerEventSchema.parse(e));
  let applied = 0;
  let replaced = 0;
  const affectedPlayerIds = new Set<string>();

  await db.transaction('rw', db.playerEvents, db.players, async () => {
    for (const event of validated) {
      const existing = await db.playerEvents.get(event.id);
      if (existing) {
        // Conflict resolution: last-write-wins (timestamp + userId)
        if (shouldReplacePlayerEvent(existing, event)) {
          await db.playerEvents.put(event);
          replaced += 1;
        }
        // Otherwise keep existing event
      } else {
        await db.playerEvents.add(event);
        applied += 1;
      }

      // Track affected player IDs for cache updates
      if (event.type === 'PlayerMerged') {
        affectedPlayerIds.add(event.payload.sourcePlayerId);
        affectedPlayerIds.add(event.payload.targetPlayerId);
      } else if ('playerId' in event.payload) {
        affectedPlayerIds.add(event.payload.playerId);
      }
    }

    // Update player cache for all affected players
    for (const playerIdStr of affectedPlayerIds) {
      try {
        const playerId = parsePlayerId(playerIdStr);
        await updatePlayerCacheFromEvents(playerId);
      } catch (error) {
        handleBackgroundError(
          error,
          { operation: 'updatePlayerCache', playerId: playerIdStr },
          'warn',
        );
      }
    }
  });

  return { applied, replaced };
}
